import { useCallback, useRef, useState } from "react";
import {
  useNavigate,
  Link,
  useSearchParams,
  createSearchParams,
} from "react-router-dom";
import { useSelector } from "react-redux";
import {
  IoIosArrowDown,
  IoIosArrowUp,
  IoMdCloseCircleOutline,
} from "react-icons/io";
import { AiOutlineUser } from "react-icons/ai";
import { FiShoppingCart, FiSearch } from "react-icons/fi";

import { DARK_THEME } from "../../../constants/logo";
import { APP_PATHS } from "../../../constants/paths";
import { SKU_SEARCH_PARAMS } from "../../../constants/sku";

import Badge from "../../ui/badge/Badge";
import UserMenu from "../../UserMenu/UserMenu";
import Logo from "../../ui/Logo/Logo";
import PriceListModal from "../../ui/Modal/PriceListModal/PriceListModal";

import logo from "../../../assets/images/logo.png";

const Header = (props) => {
  const { filterSkus, hideSearch } = props;

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [userMenuShown, setUserMenuShown] = useState(false);
  const [priceListModalShown, setPriceListModalShown] = useState(false);
  const [searchState, setSearchState] = useState({
    [SKU_SEARCH_PARAMS.SKU_ID]:
      searchParams.get(SKU_SEARCH_PARAMS.SKU_ID) || "",
    [SKU_SEARCH_PARAMS.SKU_COMMERCIAL_NAME]:
      searchParams.get(SKU_SEARCH_PARAMS.SKU_COMMERCIAL_NAME) || "",
    [SKU_SEARCH_PARAMS.IS_OFFICIAL_IMAGE]:
      searchParams.get(SKU_SEARCH_PARAMS.IS_OFFICIAL_IMAGE) === "true"
        ? true
        : undefined,
  });

  const cart = useSelector((state) => state.cart);
  const user = useSelector((state) => state.auth);

  function searchHandler(inputName) {
    if (searchState[inputName].trim() !== "") {
      navigate({
        pathname: APP_PATHS.SKUS,
        search: createSearchParams({
          [inputName]: searchState[inputName],
          ...searchState,
        }).toString(),
      });
      filterSkus({
        [inputName]: searchState[inputName],
      });
    } else {
      filterSkus({ [inputName]: null });
      searchParams.delete(inputName);
      setSearchParams(searchParams);
    }
  }

  const searchInputChangedHandler = (event) => {
    const {
      target: { name, value },
    } = event;
    setSearchState((currSearchState) => ({
      ...currSearchState,
      [name]: value,
    }));
  };

  const isOfficialImageHandler = (event) => {
    const {
      target: { name, checked },
    } = event;

    setSearchState((currSearchState) => {
      const newSearchState = { ...currSearchState, [name]: checked };
      return newSearchState;
    });

    if (checked) {
      navigate({
        pathname: APP_PATHS.SKUS,
        search: createSearchParams({
          ...searchState,
          [name]: checked,
        }).toString(),
      });
      filterSkus({ [name]: checked });
    } else {
      filterSkus({ [name]: null });
      searchParams.delete(name);
      setSearchParams(searchParams);
    }
  };

  const searchInputKeyPressedHandler = (event) => {
    const {
      target: { name },
      key,
    } = event;
    if (key !== "Enter") {
      return;
    }
    searchHandler(name);
  };

  const searchInputClearedHandler = (name) => {
    setSearchState((currSearchState) => ({ ...currSearchState, [name]: "" }));
    filterSkus({ [name]: null });
    searchParams.delete(name);
    setSearchParams(searchParams);
  };

  const cartClickedHandler = () => {
    navigate(APP_PATHS.MY_CART);
  };

  const userMenuButtonRef = useRef();
  const userMenuClickedHandler = () => {
    setUserMenuShown((prevState) => !prevState);
  };
  const closeUserMenu = useCallback(() => {
    setUserMenuShown(false);
  }, []);

  return (
    <header className="py-4 md:py-8 px-3 md:px-6 md:px-14">
      <div className="flex items-center justify-between gap-3 sm:gap-6">
        <div className="flex gap-3 sm:gap-6">
          <div
            className="relative border border-light-gray rounded-lg"
            ref={userMenuButtonRef}
          >
            <button
              className="p-3 w-full flex items-center gap-4"
              onClick={userMenuClickedHandler}
            >
              <span className="hidden md:block">
                {userMenuShown ? <IoIosArrowUp /> : <IoIosArrowDown />}
              </span>
              <span className="hidden md:block">{user.username}</span>
              <AiOutlineUser size={20} />
            </button>
            {userMenuShown && (
              <UserMenu
                className="top-14"
                closeMenu={closeUserMenu}
                userMenuButtonRef={userMenuButtonRef}
                openPriceList={() => setPriceListModalShown(true)}
              />
            )}
          </div>
          <div className="my-3 relative">
            <FiShoppingCart
              size={20}
              className="cursor-pointer"
              onClick={cartClickedHandler}
            />
            {(cart.skus.length !== 0 || cart.bundles.length !== 0) && (
              <Badge
                className="w-5 h-5 rounded-full bg-red-500 text-white text-sm font-semibold flex justify-center items-center absolute -top-4 -right-4"
                count={cart.skus.length + cart.bundles.length}
              />
            )}
          </div>
        </div>
        {!hideSearch && (
          <div className="grow flex flex-col md:flex-row gap-3">
            <div className="flex justify-around items-center">
              <label
                className="w-14 leading-tight text-sm"
                htmlFor="hasOfficialImage"
              >
                Official Image
              </label>
              <input
                type="checkbox"
                id="hasOfficialImage"
                name="hasOfficialImage"
                checked={searchState[SKU_SEARCH_PARAMS.IS_OFFICIAL_IMAGE]}
                onChange={isOfficialImageHandler}
              />
            </div>

            <div className="basis-full md:basis-1/4 flex items-center border border-light-gray focus-within:border-dark-blue rounded-md p-2">
              {searchState.skuId !== "" && (
                <IoMdCloseCircleOutline
                  size={20}
                  className="cursor-pointer"
                  onClick={searchInputClearedHandler.bind(
                    null,
                    SKU_SEARCH_PARAMS.SKU_ID
                  )}
                />
              )}
              <input
                dir="rtl"
                className="grow truncate mr-2"
                type="text"
                placeholder="ابحث عن طريق ال ID"
                name={SKU_SEARCH_PARAMS.SKU_ID}
                value={searchState[SKU_SEARCH_PARAMS.SKU_ID]}
                onChange={searchInputChangedHandler}
                onKeyDown={searchInputKeyPressedHandler}
              />
              <FiSearch
                size={20}
                className="cursor-pointer"
                onClick={searchHandler.bind(null, SKU_SEARCH_PARAMS.SKU_ID)}
              />
            </div>
            <div className="basis-full md:basis-3/4 flex items-center border border-light-gray focus-within:border-dark-blue rounded-md p-2">
              {searchState.skuCommercialName !== "" && (
                <IoMdCloseCircleOutline
                  size={20}
                  className="cursor-pointer"
                  onClick={searchInputClearedHandler.bind(
                    null,
                    SKU_SEARCH_PARAMS.SKU_COMMERCIAL_NAME
                  )}
                />
              )}
              <input
                dir="rtl"
                className="grow truncate mr-2"
                type="text"
                placeholder=" ابحث عن طريق اسم المنتج - OEM - Article Number"
                name={SKU_SEARCH_PARAMS.SKU_COMMERCIAL_NAME}
                value={searchState[SKU_SEARCH_PARAMS.SKU_COMMERCIAL_NAME]}
                onChange={searchInputChangedHandler}
                onKeyDown={searchInputKeyPressedHandler}
              />
              <FiSearch
                size={20}
                className="cursor-pointer"
                onClick={searchHandler.bind(
                  null,
                  SKU_SEARCH_PARAMS.SKU_COMMERCIAL_NAME
                )}
              />
            </div>
          </div>
        )}
        <Logo theme={DARK_THEME} classNames="hidden md:block" />
        <Link to="/" className="block md:hidden shrink-0">
          <img src={logo} alt="mtor logo" className="w-12 h-11" />
        </Link>
      </div>
      {priceListModalShown && (
        <PriceListModal closeModal={() => setPriceListModalShown(false)} />
      )}
    </header>
  );
};

export default Header;
